import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Route, Database, Zap, XCircle, CheckCircle2, Clock, GitMerge } from 'lucide-react';

/**
 * Route Reason Replay — a one-click, plain-language walkthrough of why a given
 * Explore question was answered by a pre-aggregated summary vs the transaction
 * fact. Rebuilt entirely from the route_decision the router already emitted.
 */
export const RouteExplainDialog = ({ state, onClose }) => {
  const rd = state?.rd;
  const open = !!state?.open && !!rd;

  const usingCanonical = rd && (rd.fallback_forced || rd.chosen_entity_id === rd.canonical_entity_id);
  const considered = (rd?.alternatives_considered_ok) || [];
  // The router lists the canonical/transaction fact alongside summaries in its
  // rejected list — never call that a "summary ruled out".
  const canonId = rd?.canonical_entity_id;
  const rejected = ((rd?.alternatives_rejected) || []).filter((r) => r.entity_id !== canonId);
  const chosenCost = considered.find((c) => c.entity_id === rd?.chosen_entity_id)?.cost;
  const summaryCandidates = considered.filter((c) => c.entity_id !== rd?.canonical_entity_id);

  // Build the ordered narrative steps.
  const steps = [];
  if (rd) {
    // Count distinct candidate tables actually weighed (dedupe by entity_id),
    // always including the transaction fact.
    const ids = new Set([canonId, rd.chosen_entity_id].filter(Boolean));
    considered.forEach((c) => c.entity_id && ids.add(c.entity_id));
    rejected.forEach((r) => r.entity_id && ids.add(r.entity_id));
    const total = ids.size;
    steps.push({
      icon: Database,
      title: 'Candidates evaluated',
      body: total > 1
        ? `The router weighed ${total} candidate tables that could answer this question — the transaction fact plus every summary whose grain and measures might cover it.`
        : 'The router evaluated the transaction fact for this question — no summary covered its grain and measures.',
    });

    if (rejected.length > 0) {
      steps.push({
        icon: XCircle,
        title: `${rejected.length} summar${rejected.length === 1 ? 'y' : 'ies'} ruled out`,
        list: rejected.map((r) => ({ name: r.name, detail: r.reason })),
      });
    }

    if (!usingCanonical && summaryCandidates.length > 0) {
      steps.push({
        icon: Zap,
        title: 'Lowest-cost summary won',
        body: `Among the summaries that could serve this query, ${rd.chosen_name} had the smallest estimated scan${chosenCost != null ? ` (~${chosenCost.toLocaleString()} rows)` : ''}, so the router accelerated the query with it instead of scanning the full transaction fact.`,
        list: summaryCandidates.map((c) => ({
          name: c.name,
          detail: `~${(c.cost || 0).toLocaleString()} rows est.`,
          highlight: c.entity_id === rd.chosen_entity_id,
        })),
      });
    }

    steps.push({
      icon: usingCanonical ? Database : CheckCircle2,
      title: usingCanonical ? 'Fell back to the transaction fact' : 'Served by a summary',
      body: usingCanonical
        ? `No summary could satisfy this query at the requested grain, so the router read the ${rd.chosen_name} transaction fact${rd.chosen_table ? ` (${rd.chosen_table})` : ''} directly for a correct, un-approximated answer.`
        : `${rd.chosen_name}${rd.chosen_table ? ` (${rd.chosen_table})` : ''} is a pre-aggregated table matching the requested grain and measures — results come back without re-scanning raw transactions.`,
    });

    if (rd.hybrid_union) {
      steps.push({
        icon: GitMerge,
        title: 'Hybrid union applied',
        body: `The summary's data stops at its watermark, so the router unioned fresh rows from the transaction fact on top of the summary to keep the answer current (policy: ${rd.hybrid_policy_applied || 'on'}).`,
      });
    }

    if (rd.watermark) {
      steps.push({
        icon: Clock,
        title: 'Freshness',
        body: `The chosen table was last refreshed at ${rd.watermark}${rd.lag_watermark_column ? ` (tracked on ${rd.lag_watermark_column})` : ''}.`,
      });
    }
  }

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-lg z-[200]" overlayClassName="z-[190]" data-testid="route-explain-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base">
            <Route className="w-4 h-4 text-primary" /> Why this route?
          </DialogTitle>
        </DialogHeader>

        {rd && (
          <div className="space-y-4">
            {/* Headline conclusion */}
            <div
              data-testid="route-explain-headline"
              className={`rounded-xl border px-3 py-2.5 text-sm leading-relaxed ${usingCanonical
                ? 'bg-sky-500/10 border-sky-500/20 text-sky-700 dark:text-sky-300'
                : 'bg-emerald-500/10 border-emerald-500/20 text-emerald-700 dark:text-emerald-300'}`}
            >
              {usingCanonical ? (
                <>This question was answered straight from the <span className="font-semibold font-mono">{rd.chosen_name}</span> transaction fact — no summary matched the requested grain.</>
              ) : (
                <>This question was <span className="font-semibold">accelerated</span> by the <span className="font-semibold font-mono">{rd.chosen_name}</span> summary rather than scanning the transaction fact.</>
              )}
            </div>

            {/* Ordered narrative */}
            <ol className="space-y-3">
              {steps.map((s, i) => {
                const Icon = s.icon;
                return (
                  <li key={i} className="flex gap-3" data-testid={`route-explain-step-${i}`}>
                    <div className="flex flex-col items-center">
                      <div className="w-6 h-6 rounded-full bg-muted/40 border border-border/40 flex items-center justify-center text-[10px] font-semibold text-muted-foreground shrink-0">{i + 1}</div>
                      {i < steps.length - 1 && <div className="w-px flex-1 bg-border/40 my-1" />}
                    </div>
                    <div className="flex-1 pb-1">
                      <p className="text-xs font-semibold flex items-center gap-1.5 text-foreground">
                        <Icon className="w-3.5 h-3.5 text-muted-foreground" /> {s.title}
                      </p>
                      {s.body && <p className="text-xs text-muted-foreground mt-0.5 leading-relaxed">{s.body}</p>}
                      {s.list && (
                        <div className="mt-1.5 space-y-1">
                          {s.list.map((item, j) => (
                            <div key={j} className={`flex items-start justify-between gap-2 px-2 py-1 rounded-lg text-[11px] ${item.highlight ? 'bg-emerald-500/10 border border-emerald-500/20' : 'bg-muted/30 border border-border/20'}`}>
                              <span className="font-mono text-foreground">{item.name}</span>
                              <span className="text-muted-foreground text-right">{item.detail}</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </li>
                );
              })}
            </ol>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default RouteExplainDialog;
